import React, { useMemo } from 'react';
import { TextField, Typography, Box } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import clsx from 'clsx';
import useStyles from 'app/cluster-styles/useStyles';
import useProviderData from '../hooks/useProviderData';

function formatInstanceDisplay(instance) {
    const name = instance.name || instance.instance_id;

    // If the name already contains spec info (from the API), don't append again
    if (name && (name.includes('vCPU') || name.includes('Mem') || name.includes('RAM'))) {
        return name;
    }

    const specs = [];
    const cpuValue = instance.vcpus || instance.vCPU || instance.vcpu || instance.cpu || instance.CPUs || instance.cores;
    if (cpuValue) specs.push(`vCPU: ${cpuValue}`);

    const memoryValue = instance.memory || instance.Memory || instance.ram || instance.RAM;
    if (memoryValue) specs.push(`Mem (GB): ${memoryValue}`);

    return specs.length > 0 ? `${name} - ${specs.join(', ')}` : name;
}

export default function InstanceSizeSelector({ provider, value, onChange, allowedInstanceIds, label = 'Instance Size', required = true, className, disabled = false }) {
    const classes = useStyles();
    const { instanceSizes } = useProviderData(provider);

    const visibleInstances = useMemo(() => {
        let list = instanceSizes;
        if (allowedInstanceIds && allowedInstanceIds.length > 0) {
            list = list.filter((s) => allowedInstanceIds.includes(s.instance_id));
        }
        return list;
    }, [instanceSizes, allowedInstanceIds]);

    const selectedInstance = useMemo(
        () => visibleInstances.find((s) => s.instance_id === value) || null,
        [visibleInstances, value]
    );

    return (
        <Autocomplete
            className={clsx(classes.input, className || "flex-1")}
            options={visibleInstances}
            getOptionLabel={(option) => formatInstanceDisplay(option)}
            getOptionSelected={(option, val) => option.instance_id === val?.instance_id}
            value={selectedInstance}
            onChange={(_, newValue) => onChange(newValue?.instance_id || '')}
            disabled={disabled}
            noOptionsText="No matching instance types"
            renderOption={(option) => (
                <Box>
                    <Typography variant="body2" style={{ fontWeight: value === option.instance_id ? 600 : 400 }}>
                        {formatInstanceDisplay(option)}
                    </Typography>
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant="outlined"
                    required={required}
                    fullWidth
                    placeholder="Search instance types..."
                />
            )}
            ListboxProps={{
                style: { maxHeight: 300 },
            }}
        />
    );
}
